import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import axios, { HttpStatusCode } from "axios";
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { JSX } from "react/jsx-runtime";
import { LoadingIcon } from "../components/ui/LoadingIcon";
import { ErrorPage } from "../features/errorPage";
import { Me, UserAccounts } from "../graphql/types";
import { getUrlName } from "../lib/Util";
import { getUserInformation } from "../networking/apiMethods";
import { useAccountContext } from "../strict/app/AccountResolver";
import { isSessionExpired } from "../strict/app/AppOverlayController";
import { requireToken } from "../strict/lib/authErrors";
import { validateAuth0ApiErrors } from "./utilsAuth0";

export enum DataGridColumns {
  ReportDateTime = "report_datetime",
  StartedFinished = "started_finished",
  Created = "created",
  Started = "started",
  Finished = "finished",
  DownloadDuration = "download_duration",
  UploadDuration = "upload_duration",
  QueuedDuration = "queued_duration",
  Project = "project",
  Name = "name",
  Purpose = "purpose",
  Description = "description",
  Status = "status",
  Actions = "action",
  Destination = "destination",
  Severity = "severity",
  Message = "message",
  ProjectName = "project_name",
  DownloadUpload = "download_upload",
}

export type DataGridPreferences = {
  columnsOrder?: string[];
  columnsWidth?: Partial<Record<DataGridColumns, number>>;
  columnVisibilityModel?: Partial<Record<DataGridColumns, boolean>>;
};
export type DataGrids =
  | "downloads"
  | "uploads"
  | "uploadsForDownload"
  | "downloadsForUpload"
  | "events"
  | "downloadsFromEvent"
  | "uploadsFromEvent";

export interface Preferences {
  prefersMiniNavbar: boolean;
  mode: "system" | "light" | "dark";
}
// Centralize the decision of saving the preferences in the database.
// This is useful if we want to run with a readonly connection to prod, in which case we cannot update the db.
export const savePreferencesInDb = true;

export type UserAccountSummary = Pick<UserAccounts, "urlName" | "name" | "show_download_summary">;

export interface User extends Omit<Me, "accounts"> {
  accounts: UserAccountSummary[];
  isAuthenticated: boolean;
  datagridPreferences: Record<DataGrids, DataGridPreferences>; // TODO: this will be removed in https://github.com/switchboard-software/distillerizer/issues/20803
  userPreferences: Preferences;
}

const initialStateUser: User = {
  isAuthenticated: false,
  default_url_name: null,
  accounts: [],
  email: "",
  name: "",
  permissions: [],
  roles: [],
  datagridPreferences: {
    downloads: {},
    uploads: {},
    uploadsForDownload: {},
    downloadsForUpload: {},
    events: {},
    downloadsFromEvent: {},
    uploadsFromEvent: {},
  },
  userPreferences: {
    prefersMiniNavbar: false,
    mode: "system",
  },
};

export const UserContext = React.createContext<{
  user: User;
  loading: boolean;
  error?: unknown;
  updateDataGridPreferences: (key: DataGrids, value: DataGridPreferences) => void;
  updateAccounts: (accounts: UserAccountSummary[]) => void;
  updateUserPreference: (key: string, value: boolean | string) => void;
}>({
  user: initialStateUser,
  loading: false,
  error: null,
  updateDataGridPreferences: () => {},
  updateAccounts: () => {},
  updateUserPreference: () => {},
});

export function UserProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User>(initialStateUser);

  const { setAccountName } = useAccountContext();

  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState<boolean>(false);
  const fetchingRef = useRef(false);
  // Tracks the urlName for which user info was last successfully fetched.
  // Used to detect account switches so we re-fetch user info for the new account.
  // Before PR #22937 this was appContext.urlName (a separately-tracked state value that lagged
  // behind the URL); using urlName from AccountResolver (which reads the URL directly) broke
  // the detection because getUrlName() and urlName were always equal.
  const [fetchedForUrlName, setFetchedForUrlName] = useState<string | null>(null);
  const location = useLocation();
  const currentUrlName = getUrlName();
  const { getAccessTokenSilently, error: auth0Error, isLoading, isAuthenticated } = useAuth0();

  const navigate = useNavigate();
  useEffect(() => {
    const removeCodeFromURL = async () => {
      try {
        await getAccessTokenSilently();

        // Auth0 is returning some information in the url, we want to clean it but we are using hash routing so
        // it's not removing the extra part but adding more to the hash url
        if (window.location.href.includes("code")) {
          window.history.replaceState(null, "", `${window.location.origin}/${window.location.hash}`);
        }
      } catch {
        // Token errors (login_required, consent_required) are handled by the overlay controller in GraphQLShell
      }
    };
    if (isLoading) return;
    removeCodeFromURL();
  }, [getAccessTokenSilently, isLoading, isAuthenticated, auth0Error]);

  const isAdminPage = () => {
    // Check of 'app' is part of the hash, as admin pages do not include that.
    return !window.location.hash.includes("app");
  };

  useEffect(() => {
    if (
      isLoading ||
      !isAuthenticated ||
      fetchingRef.current ||
      (user.isAuthenticated && (currentUrlName === fetchedForUrlName || isAdminPage())) ||
      error === HttpStatusCode.Unauthorized
    )
      return;

    fetchingRef.current = true;
    const datagridPreferences = localStorage.getItem("datagrid-preferences");

    async function handleGetUserInformation() {
      setLoading(true);
      let accessToken = "";
      try {
        accessToken = requireToken(await getAccessTokenSilently());
      } catch {
        // Token errors are handled by the overlay controller
      }
      try {
        const response = await getUserInformation(currentUrlName, accessToken);
        // Invalid/expired token → session-expired overlay, not a competing full-page logout.
        validateAuth0ApiErrors(response, navigate);
        if (!currentUrlName && !isAdminPage()) {
          navigate(`/app/${response.data.default_url_name}/dashboard`);
        }

        if (!isAdminPage()) {
          setAccountName(response.data.name);
          setFetchedForUrlName(currentUrlName);
        }

        setUser(
          Object.assign(
            {
              isAuthenticated: true,
              datagridPreferences: datagridPreferences ? JSON.parse(datagridPreferences) : {},
              userPreferences: user.userPreferences,
            },
            response.data
          )
        );
        Sentry.setUser({ email: response.data.email });
        setLoading(false);
      } catch (promiseError) {
        setUser({
          ...user,
          isAuthenticated: false,
        });
        setLoading(false);
        const status = axios.isAxiosError(promiseError) ? promiseError.response?.status : undefined;
        if (status === HttpStatusCode.Forbidden) {
          navigate(user.default_url_name ? `/app/${user.default_url_name}/dashboard` : "/");
        } else if (status === HttpStatusCode.Unauthorized) {
          // Defer to the session-expired overlay when already expired.
          if (!isSessionExpired()) {
            navigate("/error");
            setError(HttpStatusCode.Unauthorized);
          }
        }
      } finally {
        fetchingRef.current = false;
        setLoading(false);
      }
    }

    handleGetUserInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUrlName, isLoading, isAuthenticated]);

  // If we change page, reset the error state. Unless we are in an error page.
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  if (prevPathname !== location.pathname) {
    setPrevPathname(location.pathname);
    if (error && error !== HttpStatusCode.Unauthorized) setError(null);
  }

  const updateUserPreference = useCallback((key: string, value: boolean | string) => {
    setUser((user) => {
      return {
        ...user,
        userPreferences: {
          ...user.userPreferences,
          [key]: value,
        },
      };
    });
  }, []);

  const updateDataGridPreferences = useCallback((key: DataGrids, value: DataGridPreferences) => {
    setUser((user) => {
      const datagridPreferences = user.datagridPreferences;
      datagridPreferences[key] = value;
      localStorage.setItem("datagrid-preferences", JSON.stringify(datagridPreferences));
      return { ...user, datagridPreferences };
    });
  }, []);

  const updateAccounts = useCallback(
    (accounts: UserAccountSummary[]) => {
      let defaultUrlName = user.default_url_name || undefined;
      if (!accounts.some((account) => account.urlName === defaultUrlName)) {
        defaultUrlName = accounts[0]?.urlName;
      }
      setUser((user) => {
        return {
          ...user,
          default_url_name: defaultUrlName,
          accounts,
        };
      });
    },
    [user.default_url_name]
  );

  // Make the provider update only when it should.
  // We only want to force re-renders if the user,
  // loading or error states change.
  //
  // Whenever the `value` passed into a provider changes,
  // the whole tree under the provider re-renders, and
  // that can be very costly! Even in this case, where
  // you only get re-renders when logging in and out
  // we want to keep things very performant.

  const memoedValue = useMemo(() => {
    return {
      user,
      loading,
      error,
      updateDataGridPreferences,
      updateAccounts,
      updateUserPreference,
    };
  }, [user, loading, error, updateDataGridPreferences, updateAccounts, updateUserPreference]);

  if (error === HttpStatusCode.Unauthorized) return <ErrorPage />;

  if (isLoading || !isAuthenticated || loading || !user.isAuthenticated)
    return <LoadingIcon boxWidth="100vw" boxHeight="100vh" />;

  return <UserContext.Provider value={memoedValue}>{children}</UserContext.Provider>;
}

// Let's only export the `useAuth` hook instead of the context.
// We only want to use the hook directly and never the context component.
export default function useAuth() {
  return useContext(UserContext);
}
